import type { LOBEnumType, SubmissionStatus } from "@ca/db/schema";
import type { SelectItem } from "@nuxt/ui";
import type { DateRange } from "reka-ui";

import {
	computed, ref, shallowRef, 
} from "vue";

import { CalendarDate, getLocalTimeZone } from "@internationalized/date";

export const viewingDoc = ref(false);

const now = new Date();
export const dateRange = shallowRef<DateRange>({
	end: new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate()),
	start: new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate()).subtract({
		days: 180,
	}),
});

export const submissionsDateQuery = computed(() => {
	const until = dateRange.value.end?.toDate(getLocalTimeZone());
	until?.setHours(23, 59, 59, 999);
	return {
		since: dateRange.value.start?.toDate(getLocalTimeZone()).toISOString(),
		until: until?.toISOString(),
	};
});

export  const assignedToIds = ref<string[]>([]);
export const brokerageIds = ref<string[]>([]);
export const statusFilter = ref<SubmissionStatus[]>([]);
export const lobFilter = ref<LOBEnumType[]>([]);
export const targetFilter = ref<boolean>(false);
export const newPurchaseFilter = ref<boolean>(false);

export const submissionsQueryKey = computed(() => {
	return [
		"submissions",
		targetFilter.value ? "true" : "false",
		newPurchaseFilter.value ? "true" : "false",
		assignedToIds.value.join(","),
		brokerageIds.value.join(","),
		statusFilter.value.join(","),
		lobFilter.value.join(","),
		submissionsDateQuery.value.since,
		submissionsDateQuery.value.until,
	];
});

export const submissionsFlameGraphQueryKey = computed(() => {
	return [
		"submissions-flame-graph",
		submissionsDateQuery.value.since,
		submissionsDateQuery.value.until,
	];
});

export const submissionsPipelineQueryKey = computed(() => {
	return [
		"submissions-pipeline",
		submissionsDateQuery.value.since,
		submissionsDateQuery.value.until,
	];
});


export const statusItems = [
	{
		label: "New",
		value: "new",
		bg: "bg-primary-500",
		chip: {
			color: "primary",
		},
	},
	{
		label: "In Progress",
		value: "in_progress",
		bg: "bg-info-500",
		chip: {
			color: "info",
		},
	},
	{
		label: "Indicated",
		value: "indicated",
		bg: "bg-violet-500",
		chip: {
			color: "secondary",
			ui: {
				base: "bg-violet-500",
			},
		},
	},
	{
		label: "Quoted",
		value: "quoted",
		bg: "bg-fuchsia-500",
		chip: {
			color: "secondary",
			ui: {
				base: "bg-fuchsia-500",
			},
		},
	},
	{
		label: "Bound",
		value: "bound",
		bg: "bg-success-500",
		chip: {
			color: "success",
		},
	},
	{
		label: "Quoted Not Bound",
		value: "quoted_not_bound",
		bg: "bg-orange-500",
		chip: {
			color: "warning",
			ui: {
				base: "bg-orange-500",
			},
		},
	},
	{
		label: "Declined",
		value: "declined",
		bg: "bg-error-500",
		chip: {
			color: "error",
		},
	},
	{
		label: "Archived",
		value: "archived",
		bg: "bg-neutral-500",
		chip: {
			color: "neutral",
		},
	},
] satisfies SelectItem[];

export const lobShortLabels: Record<LOBEnumType, string> = {
	general_aviation: "GA",
	product_liability: "PL",
	commercial_general_liability: "CGL",
	uas: "UAS",
};

export const lobIcons: Record<LOBEnumType, string> = {
	general_aviation: "ic-round-airplane-ticket",
	product_liability: "ic-round-build",
	commercial_general_liability: "ic-round-warehouse",
	uas: "mdi-quadcopter",
};

export const lobIcon = (lob?: LOBEnumType | null) => lobIcons[lob ?? "general_aviation"];

export const lobItems = [
	{
		label: "General Aviation",
		icon: "ic-round-airplanemode-active",
		value: "general_aviation",
	},
	{
		label: "Product Liability",
		icon: "ic-round-build",
		value: "product_liability",
	},
	{
		label: "Commercial General Liability",
		icon: "ic-round-inventory-2",
		value: "commercial_general_liability",
	},
	{
		label: "UAS",
		icon: "material-symbols:drone-rounded",
		value: "uas",
	},
] satisfies SelectItem[];
